/**
 * PlacementsHintChip — compact search-card badge that flags which
 * placements deep-dive a course lands on (Ireland/UK/IT/FR/DE/NL/CH/
 * ES/PT), mirroring the CAO / UCAS chip UX so families spot
 * international courses at a glance without opening the detail page.
 *
 * Renders `null` for degrees whose country doesn't have a curated
 * hub dictionary — and for degrees inside such a country whose
 * *university city* isn't one of the named hub cities. Each region's
 * label names specific cities (e.g. "London hubs" for the UK, "Paris
 * IB+consulting" for France), so a chip on a University of Manchester
 * card or a Sciences-Po-Grenoble card would be factually wrong. The
 * `cities` guard prevents that.
 *
 * When the parent passes a `degree` with an `id` (or `degree_id`) the
 * chip renders as a `<Link>` that jumps straight to the placements
 * deep-dive card on the DegreeDetail page (`#placements-deep-dive`),
 * so tapping the pill on a search/shortlist/compare card lands the
 * reader on the deep-dive without a manual scroll. Falls back to a
 * plain `<span>` when no id is present (e.g. degree stub previews).
 */

import { Link } from "react-router-dom";

// Each entry names the cities the label actually refers to. The chip
// only renders when the university's own `city` matches one of them
// (case-insensitive). This is the fix for the systemic "London hubs
// on a Manchester card" bug — the previous logic keyed on country
// prefix alone.
const REGION_HINTS = [
  { prefix: "ie", label: "Dublin+Cork pipeline",  flag: "🇮🇪", cities: ["Dublin", "Cork"],
    tint: "bg-emerald-50 border-emerald-200 text-emerald-900 hover:bg-emerald-100" },
  { prefix: "uk", label: "London hubs",           flag: "🇬🇧", cities: ["London"],
    tint: "bg-[#F5F0FF] border-[#4C1D95]/25 text-[#4C1D95] hover:bg-[#EDE4FF]" },
  { prefix: "it", label: "Milan finance+luxury",  flag: "🇮🇹", cities: ["Milan", "Milano"],
    tint: "bg-rose-50 border-rose-200 text-rose-900 hover:bg-rose-100" },
  { prefix: "fr", label: "Paris IB+consulting",   flag: "🇫🇷", cities: ["Paris"],
    tint: "bg-sky-50 border-sky-200 text-sky-900 hover:bg-sky-100" },
  { prefix: "de", label: "Frankfurt+München",     flag: "🇩🇪", cities: ["Frankfurt", "Frankfurt am Main", "Munich", "München"],
    tint: "bg-amber-50 border-amber-200 text-amber-900 hover:bg-amber-100" },
  { prefix: "nl", label: "Eindhoven+Amsterdam",   flag: "🇳🇱", cities: ["Amsterdam", "Eindhoven"],
    tint: "bg-orange-50 border-orange-200 text-orange-900 hover:bg-orange-100" },
  { prefix: "ch", label: "Zürich+Basel",          flag: "🇨🇭", cities: ["Zurich", "Zürich", "Basel"],
    tint: "bg-red-50 border-red-200 text-red-900 hover:bg-red-100" },
  { prefix: "es", label: "Madrid+Barcelona",      flag: "🇪🇸", cities: ["Madrid", "Barcelona"],
    tint: "bg-yellow-50 border-yellow-300 text-yellow-900 hover:bg-yellow-100" },
  { prefix: "pt", label: "Lisbon+Porto pipeline", flag: "🇵🇹", cities: ["Lisbon", "Lisboa", "Porto", "Oporto"],
    tint: "bg-green-50 border-green-300 text-green-900 hover:bg-green-100" },
];

// Case-insensitive city match — universities occasionally store the
// city with different casing / spelling (München vs Munich, Lisboa vs
// Lisbon), which the `cities` list covers explicitly above.
function _cityMatches(degreeCity, cities) {
  if (!degreeCity) return false;
  const needle = String(degreeCity).trim().toLowerCase();
  return cities.some((c) => c.toLowerCase() === needle);
}


export default function PlacementsHintChip({ degree, testid, href }) {
  if (!degree) return null;
  const uidPrefix = (degree.university_id || "").split("-")[0].toLowerCase();
  const hint = REGION_HINTS.find((h) => h.prefix === uidPrefix);
  if (!hint) return null;

  // Guard: only render when the university's city is one of the cities
  // this hint actually names. Prevents "London hubs" on a Manchester
  // card, "Paris IB+consulting" on Sciences-Po-Grenoble, etc. Reads
  // `degree.city` first (populated on Advanced-Search / Fast-Match
  // hydration), then falls back to `degree.university?.city` if the
  // parent handed us the full uni object.
  const degreeCity = degree.city
    || degree.university?.city
    || degree.uni?.city
    || null;
  if (!_cityMatches(degreeCity, hint.cities)) return null;

  const degreeId = degree.id || degree.degree_id;
  const testId = testid || `placements-hint-${uidPrefix}`;
  const className = `inline-flex items-center gap-1 rounded-full border ${hint.tint} text-[10px] font-mono px-2 py-0.5 transition-colors cursor-pointer`;
  const title = "Jump to the placements deep-dive on the degree page";
  const inner = (
    <>
      <span aria-hidden>{hint.flag}</span>
      <span>{hint.label}</span>
      <span aria-hidden className="text-[9px] opacity-70">→</span>
    </>
  );

  // Precedence: explicit `href` prop > degree-scoped deep-dive link > static span.
  const to = href || (degreeId ? `/degree/${degreeId}#placements-deep-dive` : null);
  if (to) {
    return (
      <Link
        to={to}
        data-testid={testId}
        className={className}
        title={title}
        onClick={(e) => e.stopPropagation()}
      >
        {inner}
      </Link>
    );
  }

  // No degree id and no override — surface the flag+region as a
  // static span so the pipeline story still lands, just not clickable.
  return (
    <span
      data-testid={testId}
      className={`inline-flex items-center gap-1 rounded-full border ${hint.tint} text-[10px] font-mono px-2 py-0.5`}
      title="Placements deep-dive on the degree page"
    >
      <span aria-hidden>{hint.flag}</span>
      <span>{hint.label}</span>
    </span>
  );
}
